import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "@/components/ui/card";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { 
  AlertTriangle, 
  Filter, 
  Download, 
  Search, 
  Calendar,
  ArrowUpDown,
  Eye,
  RefreshCw
} from "lucide-react";
import { getOwnerDiscrepancies } from "@/lib/api";
import { getCurrentOwner } from "@/lib/auth";
import { formatCurrency, getCurrencyName } from "@/lib/currency";
import { toast } from "@/hooks/use-toast";

export default function OwnerDiscrepancies() {
  const owner = getCurrentOwner();
  const [search, setSearch] = useState("");
  const [daysFilter, setDaysFilter] = useState("30");
  const [sortBy, setSortBy] = useState<"date" | "amount">("date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [selectedDiscrepancy, setSelectedDiscrepancy] = useState<any>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

    // Resetear página cuando cambian filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [search, daysFilter, sortBy, sortOrder]);

  const { 
    data: discrepanciesData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ["owner-discrepancies-full", owner?.id, daysFilter],
    queryFn: () => getOwnerDiscrepancies(owner!.id),
    enabled: !!owner,
  });

    const handleExport = () => {
    if (discrepancies.length === 0) {
      toast({
        title: "Sin datos",
        description: "No hay discrepancias para exportar",
        variant: "destructive",
      });
      return;
    }
    
    // Crear contenido CSV
    const headers = [
      'Fecha',
      'Tienda', 
      'Caja',
      'Cajero',
      'Efectivo Esperado',
      'Efectivo Contado',
      'Diferencia',
      'Tipo',
      'Explicación'
    ].join(',');
    
    const rows = discrepancies.map(d => [
      new Date(d.createdAt).toISOString(),
      `"${d.shopName}"`,
      `"${d.cashRegisterName}"`,
      `"${d.closedBy}"`,
      d.expectedCash,
      d.countedCash,
      d.cashDifference,
      d.discrepancyType,
      d.notes ? `"${d.notes.replace(/"/g, '""')}"` : 'Sin explicación'
    ].join(','));
    
    const csvContent = [headers, ...rows].join('\n');
    
    // Crear y descargar archivo
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `discrepancias_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "Exportación completada",
      description: `Se exportaron ${discrepancies.length} discrepancias`,
    });
  };

    const handleClearFilters = () => {
    setSearch("");
    setDaysFilter("30");
    setSortBy("date");
    setSortOrder("desc");
    setCurrentPage(1);
    
    toast({
      title: "Filtros limpiados",
      description: "Todos los filtros han sido restablecidos",
    });
  };

  const handleSort = (field: "date" | "amount") => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  if (!owner) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">No hay información del dueño</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertTriangle className="h-12 w-12 text-destructive" />
        <p className="text-lg font-medium">Error al cargar discrepancias</p>
        <p className="text-muted-foreground text-center">
          No se pudieron cargar las discrepancias de caja.
        </p>
        <Button onClick={() => refetch()}>
          Reintentar
        </Button>
      </div>
    );
  }

  const discrepancies = discrepanciesData?.discrepancies || [];
  
  // Filtrar por búsqueda
  const filteredDiscrepancies = discrepancies.filter(d =>
    d.shopName.toLowerCase().includes(search.toLowerCase()) ||
    d.closedBy.toLowerCase().includes(search.toLowerCase()) ||
    d.notes?.toLowerCase().includes(search.toLowerCase())
  );

  // Ordenar
  const sortedDiscrepancies = [...filteredDiscrepancies].sort((a, b) => {
    if (sortBy === "date") {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
    } else {
      const amountA = Math.abs(parseFloat(a.cashDifference));
      const amountB = Math.abs(parseFloat(b.cashDifference));
      return sortOrder === "desc" ? amountB - amountA : amountA - amountB;
    }
  });

    // Paginación
  const totalPages = Math.ceil(sortedDiscrepancies.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedDiscrepancies = sortedDiscrepancies.slice(startIndex, endIndex);

  // Calcular estadísticas
  const stats = {
    total: discrepancies.length,
    faltantes: discrepancies.filter(d => d.discrepancyType === 'faltante').length,
    sobrantes: discrepancies.filter(d => d.discrepancyType === 'sobrante').length,
    totalFaltantes: discrepancies
      .filter(d => d.discrepancyType === 'faltante')
      .reduce((sum, d) => sum + Math.abs(parseFloat(d.cashDifference)), 0),
    totalSobrantes: discrepancies
      .filter(d => d.discrepancyType === 'sobrante')
      .reduce((sum, d) => sum + Math.abs(parseFloat(d.cashDifference)), 0),
    promedioFaltante: discrepancies.filter(d => d.discrepancyType === 'faltante').length > 0
      ? discrepancies
          .filter(d => d.discrepancyType === 'faltante')
          .reduce((sum, d) => sum + Math.abs(parseFloat(d.cashDifference)), 0) /
        discrepancies.filter(d => d.discrepancyType === 'faltante').length
      : 0,
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => window.history.back()}
            className="h-8 w-8 p-0"
          >
            ←
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
              Discrepancias de Caja
            </h1>
            <p className="text-muted-foreground">
              Monitoreo completo de diferencias en todas tus tiendas • Moneda: {getCurrencyName(owner?.currency || 'CUP')}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </Button>
          <Button 
            size="sm" 
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            {isLoading ? 'Actualizando...' : 'Actualizar'}
        </Button>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Discrepancias
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-sm text-muted-foreground mt-1">
              Últimos {daysFilter} días
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Faltantes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.faltantes}</div>
            <div className="text-sm text-muted-foreground mt-1">
              Total: {formatCurrency(stats.totalFaltantes, owner.currency)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Sobrantes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{stats.sobrantes}</div>
            <div className="text-sm text-muted-foreground mt-1">
              Total: {formatCurrency(stats.totalSobrantes, owner.currency)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Promedio Faltante
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(stats.promedioFaltante, owner.currency)}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              Por discrepancia
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg">Filtros</CardTitle>
            {(search || daysFilter !== "30" || sortBy !== "date" || sortOrder !== "desc") && (
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                Filtros activos
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Buscar por tienda, cajero o explicación..."
                  className="pl-9"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <select
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={daysFilter}
                  onChange={(e) => setDaysFilter(e.target.value)}
                >
                  <option value="7">Últimos 7 días</option>
                  <option value="30">Últimos 30 días</option>
                  <option value="90">Últimos 90 días</option>
                  <option value="365">Último año</option>
                </select>
              </div>
              <Button 
                variant="outline" 
                size="icon"
                onClick={handleClearFilters}
                title="Limpiar todos los filtros"
                disabled={!search && daysFilter === "30" && sortBy === "date" && sortOrder === "desc"}
              >
                <span className="text-lg">✕</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabla de discrepancias */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Discrepancias Detalladas</CardTitle>
              <CardDescription>
                {sortedDiscrepancies.length} de {discrepancies.length} discrepancias
              </CardDescription>
            </div>
            <div className="text-sm text-muted-foreground">
              Ordenar por:
              <Button
                variant="ghost"
                size="sm"
                className="ml-2"
                onClick={() => handleSort("date")}
              >
                Fecha <ArrowUpDown className="h-3 w-3 ml-1" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="ml-2"
                onClick={() => handleSort("amount")}
              >
                Monto <ArrowUpDown className="h-3 w-3 ml-1" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : sortedDiscrepancies.length === 0 ? (
            <div className="text-center py-12">
              <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium">No hay discrepancias</p>
              <p className="text-muted-foreground">
                {search ? "No se encontraron resultados para tu búsqueda" : "No hay discrepancias registradas en este período"}
              </p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Tienda</TableHead>
                    <TableHead>Cajero</TableHead>
                    <TableHead>Esperado / Contado</TableHead>
                    <TableHead>Diferencia</TableHead>
                    <TableHead>Explicación</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                    {paginatedDiscrepancies.map((d) => (
                    <TableRow key={d.id} className="hover:bg-muted/50">
                      <TableCell className="font-medium">
                        {new Date(d.createdAt).toLocaleDateString('es-CU', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{d.shopName}</div>
                        <div className="text-xs text-muted-foreground">{d.cashRegisterName}</div>
                      </TableCell>
                      <TableCell>{d.closedBy}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="text-sm">
                            <span className="text-muted-foreground">Esperado:</span>{" "}
                            {formatCurrency(parseFloat(d.expectedCash), owner.currency)}
                          </div>
                          <div className="text-sm">
                            <span className="text-muted-foreground">Contado:</span>{" "}
                            {formatCurrency(parseFloat(d.countedCash), owner.currency)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={d.discrepancyType === 'faltante' ? "destructive" : "outline"}
                          className={d.discrepancyType === 'sobrante' ? "bg-amber-100 text-amber-800 border-amber-200" : ""}
                        >
                          {d.discrepancyType === 'faltante' ? '−' : '+'}
                          {formatCurrency(Math.abs(parseFloat(d.cashDifference)), owner.currency)}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        {d.notes ? (
                          <p className="text-sm line-clamp-2">{d.notes}</p>
                        ) : (
                          <span className="text-sm text-muted-foreground italic">Sin explicación</span>
                        )}
                      </TableCell>
                    <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setSelectedDiscrepancy(d);
                            setShowDetails(true);
                          }}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Detalles
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
            {/* Paginación */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6 pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Mostrando {startIndex + 1}-{Math.min(endIndex, sortedDiscrepancies.length)} de {sortedDiscrepancies.length} discrepancias
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="h-8 w-8 p-0"
                    >
                      ←
                    </Button>
                    
                    <select
                      value={currentPage}
                      onChange={(e) => setCurrentPage(Number(e.target.value))}
                      className="rounded-md border border-input bg-background px-2 py-1 text-sm h-8"
                    >
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <option key={page} value={page}>
                          {page}
                        </option>
                      ))}
                    </select>
                    
                    <span className="text-sm text-muted-foreground">de {totalPages}</span>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="h-8 w-8 p-0"
                    >
                      →
                    </Button>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Mostrar:</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="rounded-md border border-input bg-background px-2 py-1 text-sm h-8"
                    >
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </div>
                </div>
              )}
        </CardContent>
      </Card>

      {/* Resumen */}
      {discrepancies.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Resumen Análisis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium mb-2">Distribución por Tienda</h3>
                <div className="space-y-2">
                  {Array.from(
                    new Set(discrepancies.map(d => d.shopName))
                  ).map(shopName => {
                    const shopDiscrepancies = discrepancies.filter(d => d.shopName === shopName);
                    const faltantes = shopDiscrepancies.filter(d => d.discrepancyType === 'faltante').length;
                    const sobrantes = shopDiscrepancies.filter(d => d.discrepancyType === 'sobrante').length;
                    
                    return (
                      <div key={shopName} className="flex justify-between items-center border-b pb-2">
                        <span className="text-sm">{shopName}</span>
                        <div className="flex gap-2">
                          <Badge variant="destructive" className="text-xs">
                            {faltantes} faltantes
                          </Badge>
                          <Badge variant="outline" className="text-xs bg-amber-50">
                            {sobrantes} sobrantes
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <h3 className="font-medium mb-2">Recomendaciones</h3>
                <div className="space-y-3 text-sm">
                  {stats.faltantes > stats.sobrantes * 2 && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded">
                      <p className="font-medium text-red-800">⚠️ Alta tasa de faltantes</p>
                      <p className="text-red-700">
                        Considera revisar procedimientos de cierre o implementar auditorías sorpresa.
                      </p>
                    </div>
                  )}
                  {stats.promedioFaltante > 50 && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                      <p className="font-medium text-amber-800">📊 Promedio de faltante elevado</p>
                      <p className="text-amber-700">
                        El promedio de faltante ({formatCurrency(stats.promedioFaltante, owner.currency)}) sugiere necesidad de capacitación.
                      </p>
                    </div>
                  )}
                  {discrepancies.filter(d => !d.notes).length > 0 && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                      <p className="font-medium text-blue-800">📝 Falta de explicaciones</p>
                      <p className="text-blue-700">
                        {discrepancies.filter(d => !d.notes).length} discrepancias sin explicación.
                        Reforzar la importancia de registrar justificaciones.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

            {/* Modal de detalles */}
      {showDetails && selectedDiscrepancy && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">Detalles de Discrepancia</h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowDetails(false)}
              >
                ✕
              </Button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Tienda</p>
                  <p className="font-medium">{selectedDiscrepancy.shopName}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Caja</p>
                  <p className="font-medium">{selectedDiscrepancy.cashRegisterName}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Cajero</p>
                  <p className="font-medium">{selectedDiscrepancy.closedBy}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Fecha</p>
                  <p className="font-medium">
                    {new Date(selectedDiscrepancy.createdAt).toLocaleString()}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Efectivo Esperado</p>
                  <p className="font-medium">
                    {formatCurrency(parseFloat(selectedDiscrepancy.expectedCash), owner.currency)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Efectivo Contado</p>
                  <p className="font-medium">
                    {formatCurrency(parseFloat(selectedDiscrepancy.countedCash), owner.currency)}
                  </p>
                </div>
              </div>
              
              <div className={`p-3 rounded border ${
                selectedDiscrepancy.discrepancyType === 'faltante' 
                  ? 'bg-red-50 border-red-200' 
                  : 'bg-yellow-50 border-yellow-200'
              }`}>
                <p className="text-sm text-muted-foreground">Diferencia</p>
                <p className={`text-xl font-bold ${
                  selectedDiscrepancy.discrepancyType === 'faltante' 
                    ? 'text-red-700' 
                    : 'text-yellow-700'
                }`}>
                  {selectedDiscrepancy.discrepancyType === 'faltante' ? '−' : '+'}
                  {formatCurrency(Math.abs(parseFloat(selectedDiscrepancy.cashDifference)), owner.currency)}
                </p>
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground">Explicación</p>
                <div className="mt-1 p-3 bg-muted/30 rounded">
                  {selectedDiscrepancy.notes || "No se proporcionó explicación"}
                </div>
              </div>
              
              <div className="pt-4 border-t">
                <Button 
                  className="w-full"
                  onClick={() => setShowDetails(false)}
                >
                  Cerrar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}